import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, Briefcase, Star, CheckCircle2, Trash2, ShieldCheck, Hammer, Coins, Flag, Ban, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/AuthContext";
import { useI18n } from "@/i18n";
import api from "@/api";
import { makeTr } from "./adminTr";
const useTr = () => makeTr(useI18n().lang);

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-[#EBDDD3] bg-white p-5">
      <span className="w-10 h-10 rounded-lg bg-[#C65338]/10 flex items-center justify-center mb-3">
        <Icon className="w-5 h-5 text-[#C65338]" />
      </span>
      <div className="font-display text-3xl font-black text-[#2D231F]">{value}</div>
      <div className="text-sm text-[#8C7A70]">{label}</div>
    </div>
  );
}

const azLetter = (name) => {
  const c = ((name || "").trim()[0] || "#").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
  return /[A-Z]/.test(c) ? c : "#";
};
const byName = (key) => (x, y) => (x[key] || "").localeCompare(y[key] || "", "de", { sensitivity: "base" });
const AzBar = ({ items, nameKey, value, onChange, testid }) => { const tr = useTr(); return (
  <div data-testid={testid} className="sticky top-24 flex flex-col items-center gap-0.5 rounded-full bg-white border border-[#EBDDD3] py-2 px-1 shrink-0">
    <button onClick={() => onChange("")} data-testid={`${testid}-all`} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${value === "" ? "bg-[#C65338] text-white" : "text-[#5A4B45] hover:text-[#C65338]"}`}>{tr("Alle")}</button>
    {[..."ABCDEFGHIJKLMNOPQRSTUVWXYZ#"].map((L) => {
      const has = items.some((x) => azLetter(x[nameKey]) === L);
      return (
        <button key={L} disabled={!has} onClick={() => onChange(value === L ? "" : L)} data-testid={`${testid}-${L}`}
          className={`w-6 h-6 text-xs font-bold rounded-full flex items-center justify-center ${value === L ? "bg-[#C65338] text-white" : has ? "text-[#2D231F] hover:bg-[#C65338]/10" : "text-[#D8CABF] cursor-default"}`}>{L}</button>
      );
    })}
  </div>
); };
const Person = ({ name, email, label }) => { const tr = useTr(); return (
  <div className="mt-1 text-xs text-[#5A4B45]">{label ? <span className="text-[#A89A90]">{tr(label)}: </span> : null}<b>{name || "—"}</b>{email ? <span className="text-[#8C7A70]"> · {email}</span> : null}</div>
); };

const LEDGER_LABEL = { purchase: "Gekauft", promo: "Start-Aktion (gratis)", grant: "Von dir geschenkt", spent: "Für Bewerbung verbraucht", refund: "Erstattet", correction: "Korrektur" };
function LeadEditor({ c, onClose, onSaved }) {
  const tr = useTr();
  const [data, setData] = useState(null);
  const [val, setVal] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/craftsmen/${c._id}/ledger`).then((r) => { setData(r.data); setVal(String(r.data.balance)); });
  useEffect(() => { load(); }, []); // eslint-disable-line
  const save = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/craftsmen/${c._id}/credits-correct`, { new_balance: parseInt(val, 10), reason });
      toast.success(r.delta === 0 ? tr("Keine Änderung") : tr("Leads korrigiert ({0})", `${r.delta > 0 ? "+" : ""}${r.delta}`));
      setReason(""); await load(); onSaved();
    } catch { toast.error(tr("Korrektur nicht möglich – bitte Zahl (0 oder mehr) und Begründung (mind. 3 Zeichen) prüfen")); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{tr("Leads korrigieren – {0}", c.name)}</DialogTitle></DialogHeader>
        {data && (
          <div className="space-y-4">
            <div className="rounded-lg border border-[#EBDDD3] p-3 space-y-2">
              <div className="text-sm text-[#5A4B45]">{tr("Aktuell aktiv:")} <b>{data.balance}</b></div>
              <div className="flex items-center gap-2">
                <span className="text-sm">{tr("Neuer Stand:")}</span>
                <input data-testid="lead-new-balance" type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} className="w-24 rounded-lg border border-[#DCC2B3] px-3 py-1.5 text-sm" />
              </div>
              <input data-testid="lead-reason" placeholder={tr("Begründung (Pflicht)")} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-lg border border-[#DCC2B3] px-3 py-1.5 text-sm" />
              <Button data-testid="lead-save" disabled={busy} onClick={save} className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white">{tr("Korrigieren")}</Button>
            </div>
            <div>
              <div className="text-sm font-semibold mb-1">{tr("Verlauf")}</div>
              <div className="space-y-1">
                {data.entries.map((e) => (
                  <div key={e._id} className="flex items-center justify-between text-xs rounded-md bg-[#FAF5F0] px-3 py-1.5">
                    <span>{tr(LEDGER_LABEL[e.type] || e.type)}{e.reason ? ` – ${e.reason}` : ""}{e.job_title ? ` – ${e.job_title}` : ""}<span className="text-[#A89A90]"> · {(e.created_at || "").slice(0, 16).replace("T", " ")}</span></span>
                    <b className={e.amount < 0 ? "text-[#B0413E]" : "text-[#3F4A2E]"}>{e.amount > 0 ? "+" : ""}{e.amount}</b>
                  </div>
                ))}
                {data.entries.length === 0 && <div className="text-xs text-[#A89A90]">{tr("Noch keine Einträge.")}</div>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const REPORT_STATUS = { pending: "Offen – wartet auf deine Prüfung", auto_confirmed: "Automatisch gesperrt – wartet auf deine Prüfung", confirmed: "Als Fake bestätigt", dismissed: "Abgelehnt (kein Fake)" };
const SEV_LABEL = { disable: "Nur Auftrag gesperrt", flag: "Auftrag gesperrt + Kunde markiert", block: "Auftrag gesperrt + Kunde blockiert" };
function reportEventText(e, tr) {
  const d = e.details || {}; const who = e.actor ? `${e.actor.name} (${e.actor.email})` : tr("System");
  if (e.type === "reported") return { title: tr("Meldung eingegangen"), text: `${tr("Ein Handwerker hat den Auftrag gemeldet.")}${d.reason ? tr(" Grund: „{0}“", d.reason) : tr(" Kein Grund angegeben.")}`, who };
  if (e.type === "auto_confirmed") return { title: tr("Automatisch gesperrt"), text: tr("{0} verschiedene Handwerker haben gemeldet. Der Auftrag wurde automatisch gesperrt, der Kunde markiert und {1} Lead(s) erstattet.", d.reporters, d.refunded), who };
  if (e.type === "confirmed") return { title: tr("Von dir als Fake bestätigt"), text: `${tr("Maßnahme: {0}. {1} Lead(s) erstattet.", tr(SEV_LABEL[d.severity] || d.severity), d.refunded)}${d.note ? tr(" Begründung: „{0}“", d.note) : tr(" Keine Begründung eingetragen.")}`, who };
  if (e.type === "dismissed") return { title: tr("Von dir abgelehnt"), text: `${tr("Auftrag bleibt bestehen.")}${d.note ? tr(" Begründung: „{0}“", d.note) : tr(" Keine Begründung eingetragen.")}`, who };
  return { title: e.type, text: "", who };
}
const USER_EVENT_TITLE = { self_deleted: "Konto selbst gelöscht", warned: "Mahnung per Mail", blocked: "Gesperrt", unblocked: "Sperre aufgehoben" };
function WarnLine({ user, events }) {
  const tr = useTr();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  const mine = (events || []).filter((e) => e.user_id === user._id).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const n = user.warning_count || 0;
  return (
    <div data-testid={`warnline-${user._id}`} className="mt-1.5 text-xs">
      <span className={n > 0 ? "font-semibold text-[#B27A1E]" : "text-[#8C7A70]"}>{tr("Mahnungen: {0}", n === 0 ? tr("keine") : `${n}${user.last_warned_at ? " " + tr("(zuletzt {0})", fmtDate(user.last_warned_at)) : ""}`)}</span>
      {mine.length > 0 && (
        <button data-testid={`warnhist-toggle-${user._id}`} onClick={() => setOpen(!open)} className="ml-3 underline text-[#C65338]">{open ? tr("Verlauf ausblenden") : tr("Verlauf anzeigen ({0})", mine.length)}</button>
      )}
      {open && (
        <ol data-testid={`warnhist-${user._id}`} className="mt-2 space-y-2 border-l-2 border-[#EBDDD3] ml-1.5 pl-4">
          {mine.map((e) => (
            <li key={e._id} className="relative">
              <span className="absolute -left-[22px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#C65338]" />
              <div className="font-semibold text-[#2D231F]">{tr(USER_EVENT_TITLE[e.type] || e.type)} <span className="font-normal text-[#8C7A70]">· {fmtDate(e.created_at)} · {tr("von")} {e.actor?.name || tr("System")}{e.mail_sent === true ? tr(" · Mail gesendet") : e.mail_sent === false ? tr(" · Mail nicht gesendet") : ""}</span></div>
              {e.reason && <div className="text-[#5A4B45]">{tr("Grund: {0}", e.reason)}</div>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
const EVENT_LABEL = { ordered: "Bestellt", confirmed: "Freigegeben", cancelled: "Storniert", granted: "Gutschrift (Admin)", corrected: "Korrektur (Admin)" };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "medium" }); } catch { return iso || ""; } };
function downloadHistoryCsv(hist) {
  const all = [...hist.orders.flatMap((o) => o.events), ...hist.adjustments].sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  const head = ["Zeitpunkt (UTC)", "Ereignis", "Referenz", "Handwerker Name", "Handwerker E-Mail", "Leads", "Betrag", "Waehrung", "Ausgefuehrt von", "Rolle", "E-Mail Ausfuehrender", "Hinweis", "Nachgetragen", "Pruefsumme"];
  const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const rows = all.map((e) => [e.at, EVENT_LABEL[e.kind] || e.kind, e.reference, e.craftsman_name, e.craftsman_email, e.credits, e.amount, e.currency, e.actor_name, e.actor_role, e.actor_email, e.note, e.backfilled ? "ja" : "nein", e.hash]);
  const csv = "\uFEFF" + [head, ...rows].map((r) => r.map(q).join(";")).join("\r\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  a.download = `zahlungs-historie-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function Admin() {
  const { user } = useAuth();
  const { t, catName, jt, cityName, money } = useI18n();
  const tr = useTr();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [letter, setLetter] = useState("");
  const [cLetter, setCLetter] = useState("");
  const [editC, setEditC] = useState(null);
  const [jLetter, setJLetter] = useState("");
  const [pLetter, setPLetter] = useState("");
  const [notes, setNotes] = useState({});
  const [blockT, setBlockT] = useState(null);
  const [grantT, setGrantT] = useState(null);
  const [grantAmt, setGrantAmt] = useState("5");
  const [grantReason, setGrantReason] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [blockMail, setBlockMail] = useState(true);
  const [blockBusy, setBlockBusy] = useState(false);
  const [blockHist, setBlockHist] = useState([]);
  const [bLetter, setBLetter] = useState("");
  const [contacts, setContacts] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [atLetter, setAtLetter] = useState("");
  const [cnLetter, setCnLetter] = useState("");
  const [cnotes, setCnotes] = useState({});
  const [rLetter, setRLetter] = useState("");
  const [kLetter, setKLetter] = useState("");
  const [oLetter, setOLetter] = useState("");
  const [payHist, setPayHist] = useState({ orders: [], adjustments: [], chain_ok: true, event_count: 0 });
  const letterOf = (u) => {
    const c = ((u.name || u.email || "").trim()[0] || "#").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
    return /[A-Z]/.test(c) ? c : "#";
  };
  const [jobs, setJobs] = useState([]);
  const [craftsmen, setCraftsmen] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [creditsData, setCreditsData] = useState({ craftsmen: [], transactions: [] });
  const [reports, setReports] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sev, setSev] = useState({});

  useEffect(() => {
    if (user === false || (user && user.role !== "admin")) nav("/");
  }, [user, nav]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/users").then((r) => setUsers([...r.data].sort((x, y) => (x.name || x.email || "").localeCompare(y.name || y.email || "", "de", { sensitivity: "base" }))));
    api.get("/admin/contact-attempts").then((r) => setAttempts([...r.data].sort(byName("user_name"))));
    api.get("/admin/contacts").then((r) => setContacts([...r.data].sort(byName("name"))));
    api.get("/admin/block-history").then((r) => setBlockHist([...r.data].sort(byName("user_name"))));
    api.get("/admin/jobs").then((r) => setJobs([...r.data].sort(byName("customer_name"))));
    api.get("/admin/craftsmen").then((r) => setCraftsmen([...r.data].sort((x, y) => (x.name || "").localeCompare(y.name || "", "de", { sensitivity: "base" }))));
    api.get("/admin/reviews").then((r) => setReviews([...r.data].sort(byName("customer_name"))));
    api.get("/admin/credits").then((r) => setCreditsData({ ...r.data, craftsmen: [...r.data.craftsmen].sort(byName("name")) }));
    api.get("/admin/reports").then((r) => setReports(r.data));
    api.get("/admin/credit-orders").then((r) => setOrders(r.data));
    api.get("/admin/payment-history").then((r) => setPayHist({ ...r.data, orders: [...r.data.orders].sort(byName("craftsman_name")) }));
  };
  useEffect(() => { if (user && user.role === "admin") loadAll(); }, [user]);

  if (!user || user === false || user.role !== "admin") return <div className="py-24 text-center text-[#A89A90]">…</div>;

  const del = async (url, msg) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    try {
      await api.delete(url);
      toast.success(msg);
    } catch (e) {
      if (e?.response?.data?.detail === "HAS_RECORDS") toast.error(tr("Löschen nicht möglich: Zu diesem Konto gibt es Zahlungsdaten, die aufbewahrt werden müssen. Bitte stattdessen sperren."));
      else toast.error(tr("Löschen nicht möglich."));
    }
    loadAll();
  };
  const verify = async (cid) => {
    await api.post(`/admin/craftsmen/${cid}/verify`);
    loadAll();
  };
  const grant = (cid, name) => { setGrantAmt("5"); setGrantReason(""); setGrantT({ cid, name }); };
  const doGrant = async () => {
    setGrantBusy(true);
    try {
      await api.post(`/admin/craftsmen/${grantT.cid}/credits`, { amount: parseInt(grantAmt, 10), reason: grantReason.trim() });
      toast.success(tr("Leads gutgeschrieben und mit Begründung protokolliert."));
      setGrantT(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "REASON_REQUIRED" ? tr("Bitte eine Begründung mit mindestens 10 Zeichen eintragen.") : tr("Gutschrift nicht möglich."));
    } finally { setGrantBusy(false); }
  };
  const confirmOrder = async (id) => {
    await api.post(`/admin/credit-orders/${id}/confirm`);
    toast.success(`${t("admin.confirm")} ✓`);
    loadAll();
  };
  const cancelOrder = async (id) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    await api.post(`/admin/credit-orders/${id}/cancel`);
    loadAll();
  };
  const resolveReport = async (rid, action, severity) => {
    await api.post(`/admin/reports/${rid}/resolve`, { action, severity: severity || "disable", note: (notes[rid] || "").trim() });
    toast.success(t("admin.reportResolved"));
    loadAll();
  };
  const resendMail = async (uid) => {
    try {
      const { data } = await api.post(`/admin/users/${uid}/resend-mail`);
      if (data.ok) toast.success(t("admin.mailSent")); else toast.error(t("admin.mailFailed"));
    } catch { toast.error(t("admin.mailFailed")); }
  };
  const remindCredits = async (uid) => {
    try {
      const { data } = await api.post(`/admin/users/${uid}/remind-credits`);
      if (data.ok) toast.success(t("admin.mailSent")); else toast.error(t("admin.mailFailed"));
    } catch { toast.error(t("admin.mailFailed")); }
  };
  const warnUser = async (usr) => {
    const n = (usr.warning_count || 0) + 1;
    if (!window.confirm(tr("{0} ({1}) freundlich per Mail ermahnen ({2}. Mahnung)? Die Mail weist höflich auf den Verstoß gegen die Richtlinien hin und kündigt bei Wiederholung eine Sperre an.", usr.name, usr.email, n))) return;
    try {
      const { data } = await api.post(`/admin/users/${usr._id}/warn`);
      toast.success(data.mail_sent ? tr("{0}. Mahnung wurde per Mail gesendet.", data.warning_count) : tr("Mahnung gespeichert, aber die Mail konnte nicht gesendet werden."));
    } catch { toast.error(tr("Mahnung nicht möglich.")); }
    loadAll();
  };
  const sendTermsNotice = async () => {
    try {
      const { data } = await api.post("/admin/terms-notice", null, { params: { dry_run: true } });
      if (!data.recipients) { toast.info(tr("Keine Nutzer zu informieren – alle haben die aktuellen AGB.")); return; }
      if (!window.confirm(tr("Die Mail zu den neuen AGB jetzt an {0} Nutzer senden?", data.recipients))) return;
      const { data: r } = await api.post("/admin/terms-notice", null, { params: { dry_run: false } });
      toast.success(tr("{0} Mail(s) gesendet, {1} fehlgeschlagen.", r.sent, r.failed));
    } catch { toast.error(tr("Versand nicht möglich.")); }
  };
  const delAttempt = async (aid) => {
    if (!window.confirm(tr("Diesen Eintrag endgültig löschen? Er ist danach nicht mehr nachvollziehbar."))) return;
    await api.delete(`/admin/contact-attempts/${aid}`);
    toast.success(tr("Eintrag gelöscht"));
    loadAll();
  };
  const resolveContact = async (cid) => {
    await api.post(`/admin/contacts/${cid}/resolve`, { note: (cnotes[cid] || "").trim() });
    toast.success(tr("Als erledigt markiert"));
    loadAll();
  };
  const toggleBlock = async (uid, name, email, blocked, prefill = "") => {
    if (blocked) {
      if (!window.confirm(tr("{0}: Sperre wirklich aufheben? Die E-Mail-Adresse wird wieder freigegeben.", name || email))) return;
      await api.post(`/admin/users/${uid}/block`, {});
      toast.success(tr("Sperre aufgehoben"));
      loadAll();
      return;
    }
    setBlockReason(prefill); setBlockMail(true); setBlockT({ uid, name, email });
  };
  const doBlock = async () => {
    setBlockBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${blockT.uid}/block`, { reason: blockReason.trim(), send_mail: blockMail });
      toast.success(data.mail_sent ? tr("Gesperrt. Mail wurde an den Nutzer gesendet.") : blockMail ? tr("Gesperrt, aber die Mail konnte nicht gesendet werden.") : tr("Gesperrt (ohne Mail)."));
      setBlockT(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "REASON_REQUIRED" ? tr("Bitte eine Begründung mit mindestens 10 Zeichen eintragen.") : tr("Sperren nicht möglich."));
    } finally { setBlockBusy(false); }
  };

  const Row = ({ children, testid }) => (
    <div data-testid={testid} className="flex items-center justify-between gap-3 rounded-lg border border-[#EBDDD3] bg-white p-4">{children}</div>
  );

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
      <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2D231F] mb-8">{t("admin.title")}</motion.h1>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Users} label={t("admin.users")} value={stats.users} />
          <StatCard icon={Hammer} label={t("admin.craftsmen")} value={stats.craftsmen} />
          <StatCard icon={Briefcase} label={t("admin.jobs")} value={stats.jobs} />
          <StatCard icon={Star} label={t("admin.reviews")} value={stats.reviews} />
        </div>
      )}
      {stats && stats.reports_pending > 0 && (
        <div className="mb-8 flex items-center gap-2 rounded-xl border border-[#B0413E]/30 bg-[#B0413E]/5 px-4 py-3 text-sm font-semibold text-[#B0413E]" data-testid="reports-pending-banner">
          <Flag className="w-4 h-4" /> {stats.reports_pending} {t("admin.reports")}
        </div>
      )}

      <Tabs defaultValue="users">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users">{t("admin.users")}</TabsTrigger>
          <TabsTrigger value="craftsmen" data-testid="admin-tab-craftsmen">{t("admin.craftsmen")}</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="admin-tab-jobs">{t("admin.jobs")}</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews">{t("admin.reviews")}</TabsTrigger>
          <TabsTrigger value="credits" data-testid="admin-tab-credits">{t("admin.credits")}</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders">
            {t("admin.orders")}
            {orders.filter((o) => o.status === "pending").length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[#E69625] text-white text-[10px] font-bold">{orders.filter((o) => o.status === "pending").length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="contacts" data-testid="admin-tab-contacts">{tr("Anfragen")}{stats && stats.contacts_pending > 0 ? ` (${stats.contacts_pending})` : ""}</TabsTrigger>
          <TabsTrigger value="blocks" data-testid="admin-tab-blocks">{tr("Sperren")}</TabsTrigger>
          <TabsTrigger value="attempts" data-testid="admin-tab-attempts">{tr("Kontaktversuche")}</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports">
            {t("admin.reports")}{stats && stats.reports_pending > 0 ? ` (${stats.reports_pending})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="mb-4 flex justify-end">
            <Button variant="outline" size="sm" data-testid="terms-notice" onClick={sendTermsNotice} className="rounded-full gap-1.5"><Mail className="w-4 h-4" />{tr("AGB-Änderung per Mail mitteilen")}</Button>
          </div>
          <div className="flex gap-3 items-start">
          <div className="flex-1 min-w-0 space-y-3">
          {users.filter((u) => !letter || letterOf(u) === letter).map((u) => (
            <Row key={u._id} testid={`admin-user-${u._id}`}>
              <div><span className="font-semibold text-[#2D231F]">{u.name}</span> <span className="text-sm text-[#8C7A70]">{u.email}</span>
                <span className="ml-2 px-2 py-0.5 rounded-full bg-[#F2E8DF] text-xs text-[#5A4B45]">{t(`role.${u.role}`) !== `role.${u.role}` ? t(`role.${u.role}`) : u.role}</span>
                {u.flagged && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#E69625]/15 text-xs font-semibold text-[#B27A1E]">{t("admin.flagged")}</span>}
                {u.email_verified === false && <span data-testid={`unverified-${u._id}`} className="ml-2 px-2 py-0.5 rounded-full bg-[#B0413E]/10 text-xs font-semibold text-[#B0413E]">{t("admin.unverified")}</span>}
                {u.blocked && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#B0413E]/10 text-xs font-semibold text-[#B0413E]">{t("admin.blocked")}</span>}
                {u.blocked && u.blocked_reason && <div data-testid={`block-reason-${u._id}`} className="mt-1 text-xs text-[#B0413E]">{tr("Grund: {0}", u.blocked_reason)}{u.blocked_at ? ` · ${fmtDate(u.blocked_at)}` : ""}</div>}
                {u.role !== "admin" && <WarnLine user={u} events={blockHist} />}
</div>
              <div className="flex items-center gap-1">
                {u.role !== "admin" && (
                  <Button variant="ghost" size="sm" data-testid={`resend-mail-${u._id}`} onClick={() => resendMail(u._id)} className="rounded-full gap-1.5 text-[#C65338]">
                    <Mail className="w-4 h-4" />{u.email_verified === false ? t("admin.resendVerify") : t("admin.resendWelcome")}
                  </Button>
                )}
                {u.role !== "admin" && (
                  <Button variant="ghost" size="sm" data-testid={`block-user-${u._id}`} onClick={() => toggleBlock(u._id, u.name, u.email, u.blocked)} className={`rounded-full gap-1.5 ${u.blocked ? "text-[#5C6646]" : "text-[#B0413E]"}`}>
                    <Ban className="w-4 h-4" />{u.blocked ? t("admin.unblock") : t("admin.block")}
                  </Button>
                )}
                {u.role !== "admin" && <Button variant="ghost" size="icon" data-testid={`del-user-${u._id}`} onClick={() => del(`/admin/users/${u._id}`, t("admin.deleted"))} className="text-destructive rounded-full"><Trash2 className="w-4 h-4" /></Button>}
              </div>
            </Row>
          ))}
          </div>
          <div data-testid="az-bar" className="sticky top-24 flex flex-col items-center gap-0.5 rounded-full bg-white border border-[#EBDDD3] py-2 px-1 shrink-0">
            <button onClick={() => setLetter("")} data-testid="az-all" className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${letter === "" ? "bg-[#C65338] text-white" : "text-[#5A4B45] hover:text-[#C65338]"}`}>{tr("Alle")}</button>
            {[..."ABCDEFGHIJKLMNOPQRSTUVWXYZ#"].map((L) => {
              const has = users.some((u) => letterOf(u) === L);
              return (
                <button key={L} disabled={!has} onClick={() => setLetter(letter === L ? "" : L)} data-testid={`az-${L}`}
                  className={`w-6 h-6 text-xs font-bold rounded-full flex items-center justify-center ${letter === L ? "bg-[#C65338] text-white" : has ? "text-[#2D231F] hover:bg-[#C65338]/10" : "text-[#D8CABF] cursor-default"}`}>{L}</button>
              );
            })}
          </div>
          </div>
        </TabsContent>

        <TabsContent value="craftsmen">
          <div className="flex gap-3 items-start">
          <div className="flex-1 min-w-0 space-y-3">
          {craftsmen.filter((c) => !cLetter || letterOf(c) === cLetter).map((c) => {
            return (
            <Row key={c._id} testid={`admin-craftsman-${c._id}`}>
              <div><div className="flex items-center gap-2"><span className="font-semibold text-[#2D231F]">{c.name}</span>
                <span className="text-sm text-[#8C7A70]">{catName(c.trade) || c.trade}</span>
                {c.verified && <CheckCircle2 className="w-4 h-4 text-[#5C6646]" />}</div>
                <div data-testid={`admin-phone-${c._id}`} className="text-xs text-[#5A4B45] mt-0.5">{tr("Telefon:")} <b>{c.phone || tr("nicht angegeben")}</b></div>
                <WarnLine user={users.find((x) => x._id === c.user_id)} events={blockHist} />
                {c.deal_stats && (
                  <div data-testid={`deal-stats-${c._id}`} className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#5A4B45]">
                    <span>{tr("Beworben:")} <b>{c.deal_stats.applied}</b></span>
                    <span>{tr("Zuschlag erhalten:")} <b>{c.deal_stats.won}</b></span>
                    <span>{tr("Abgeschlossen:")} <b>{c.deal_stats.completed}</b></span>
                    <span className="px-2 py-0.5 rounded-full bg-[#5C6646]/15 text-[#3F4A2E] font-bold">{tr("Quote: {0} %", c.deal_stats.applied ? Math.round((100 * c.deal_stats.won) / c.deal_stats.applied) : 0)}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {c.blocked && <span className="px-2 py-0.5 rounded-full bg-[#B0413E]/10 text-xs font-semibold text-[#B0413E]">{t("admin.blocked")}</span>}
                <Button variant="ghost" size="sm" data-testid={`block-craftsman-${c._id}`} onClick={() => toggleBlock(c.user_id, c.name, "", c.blocked)} className={`rounded-full gap-1.5 ${c.blocked ? "text-[#5C6646]" : "text-[#B0413E]"}`}><Ban className="w-4 h-4" />{c.blocked ? t("admin.unblock") : t("admin.block")}</Button>
                <Button variant="outline" size="sm" data-testid={`verify-${c._id}`} onClick={() => verify(c._id)} className="rounded-full gap-1.5"><ShieldCheck className="w-4 h-4" />{t("admin.verify")}</Button>
                <Button variant="ghost" size="icon" data-testid={`del-craftsman-${c._id}`} onClick={() => del(`/admin/craftsmen/${c._id}`, t("admin.deleted"))} className="text-destructive rounded-full"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </Row>
            );
          })}
          </div>
          <div data-testid="az-bar-craftsmen" className="sticky top-24 flex flex-col items-center gap-0.5 rounded-full bg-white border border-[#EBDDD3] py-2 px-1 shrink-0">
            <button onClick={() => setCLetter("")} data-testid="caz-all" className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cLetter === "" ? "bg-[#C65338] text-white" : "text-[#5A4B45] hover:text-[#C65338]"}`}>{tr("Alle")}</button>
            {[..."ABCDEFGHIJKLMNOPQRSTUVWXYZ#"].map((L) => {
              const has = craftsmen.some((c) => letterOf(c) === L);
              return (
                <button key={L} disabled={!has} onClick={() => setCLetter(cLetter === L ? "" : L)} data-testid={`caz-${L}`}
                  className={`w-6 h-6 text-xs font-bold rounded-full flex items-center justify-center ${cLetter === L ? "bg-[#C65338] text-white" : has ? "text-[#2D231F] hover:bg-[#C65338]/10" : "text-[#D8CABF] cursor-default"}`}>{L}</button>
              );
            })}
          </div>
          </div>
        </TabsContent>

        <TabsContent value="jobs">
          <div className="flex gap-3 items-start">
          <div className="flex-1 min-w-0 space-y-3">
          {jobs.filter((j) => !jLetter || azLetter(j.customer_name) === jLetter).map((j) => (
            <Row key={j._id} testid={`admin-job-${j._id}`}>
              <div><span className="font-semibold text-[#2D231F]">{jt(j, "title")}</span> <span className="text-sm text-[#8C7A70]">{cityName(j.city)}</span>
                <span className="ml-2 px-2 py-0.5 rounded-full bg-[#F2E8DF] text-xs text-[#5A4B45]">{t(`status.${j.status}`) !== `status.${j.status}` ? t(`status.${j.status}`) : j.status}</span>
                <Person label="Kunde" name={j.customer_name} email={j.customer_email} /></div>
              <Button variant="ghost" size="icon" data-testid={`del-job-${j._id}`} onClick={() => del(`/admin/jobs/${j._id}`, t("admin.deleted"))} className="text-destructive rounded-full"><Trash2 className="w-4 h-4" /></Button>
            </Row>
          ))}
          </div>
          <AzBar items={jobs} nameKey="customer_name" value={jLetter} onChange={setJLetter} testid="jaz" />
          </div>
        </TabsContent>

        <TabsContent value="reviews">
          <div className="flex gap-3 items-start">
          <div className="flex-1 min-w-0 space-y-3">
          {reviews.filter((r) => !rLetter || azLetter(r.customer_name) === rLetter).map((r) => (
            <Row key={r._id} testid={`admin-review-${r._id}`}>
              <div><span className="font-semibold text-[#2D231F]">{"★".repeat(r.rating)}</span> <span className="text-sm text-[#5A4B45]">{r.text}</span>
                {r.job_title && <div className="mt-0.5 text-xs text-[#A89A90]">{tr("Auftrag:")} {r.job_title}</div>}
                <Person label="Kunde" name={r.customer_name} email={r.customer_email} />
                <Person label="Handwerker" name={r.craftsman_name} email={r.craftsman_email} /></div>
              <Button variant="ghost" size="icon" data-testid={`del-review-${r._id}`} onClick={() => del(`/admin/reviews/${r._id}`, t("admin.deleted"))} className="text-destructive rounded-full"><Trash2 className="w-4 h-4" /></Button>
            </Row>
          ))}
          </div>
          <AzBar items={reviews} nameKey="customer_name" value={rLetter} onChange={setRLetter} testid="raz" />
          </div>
        </TabsContent>

        <TabsContent value="credits">
          <div className="flex gap-3 items-start">
          <div className="flex-1 min-w-0 space-y-3">
          {creditsData.craftsmen.filter((c) => !kLetter || azLetter(c.name) === kLetter).map((c) => {
            const usr = users.find((x) => x._id === c.user_id) || {};
            const ls = usr.lead_stats;
            return (
            <Row key={c._id} testid={`admin-credit-${c._id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#2D231F]">{c.name}</span>
                  <span className="text-sm text-[#8C7A70]">{catName(c.trade) || c.trade}</span>
                </div>
                <Person name={c.name} email={usr.email} />
                <div data-testid={`lead-stats-${c._id}`} className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#5A4B45]">
                  {ls && <span>{t("admin.leadBought")}: <b>{ls.bought}</b></span>}
                  {ls && <span>{t("admin.leadFree")}: <b>{ls.free}</b></span>}
                  {ls && ls.granted !== 0 && <span data-testid={`lead-granted-${c._id}`}>{tr("Geschenkt (von dir):")} <b>{ls.granted}</b></span>}
                  {ls && <span>{t("admin.leadUsed")}: <b>{ls.used}</b></span>}
                  {ls && ls.correction !== 0 && <span>{tr("Korrektur:")} <b>{ls.correction > 0 ? "+" : ""}{ls.correction}</b></span>}
                  <span className={`px-2 py-0.5 rounded-full font-bold ${ls && ls.low ? "bg-[#B0413E]/10 text-[#B0413E]" : "bg-[#5C6646]/15 text-[#3F4A2E]"}`}>{t("admin.leadActive")}: {c.credits ?? 0}</span>
                  {ls && ls.low && <span className="font-semibold text-[#B0413E]">{t("admin.leadLow")}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {ls && ls.low && (
                  <Button variant="ghost" size="sm" data-testid={`remind-credits-${c._id}`} onClick={() => remindCredits(c.user_id)} className="rounded-full gap-1.5 text-[#B27A1E]">
                    <Mail className="w-4 h-4" />{t("admin.remind")}
                  </Button>
                )}
                <Button variant="outline" size="sm" data-testid={`lead-edit-${c._id}`} onClick={() => setEditC(c)} className="rounded-full gap-1.5"><Coins className="w-4 h-4" />{tr("Leads korrigieren")}</Button>
                <Button variant="outline" size="sm" data-testid={`grant-${c._id}`} onClick={() => grant(c._id, c.name)} className="rounded-full gap-1.5"><Coins className="w-4 h-4" />{t("admin.grant")}</Button>
              </div>
            </Row>
            );
          })}
          </div>
          <AzBar items={creditsData.craftsmen} nameKey="name" value={kLetter} onChange={setKLetter} testid="kaz" />
          </div>
          {editC && <LeadEditor c={editC} onClose={() => setEditC(null)} onSaved={loadAll} />}
        </TabsContent>

        <TabsContent value="orders">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div data-testid="chain-status" className={`text-sm font-semibold px-3 py-1.5 rounded-full ${payHist.chain_ok ? "bg-[#5C6646]/15 text-[#3F4A2E]" : "bg-[#B0413E]/10 text-[#B0413E]"}`}>
              {payHist.chain_ok ? tr("✓ Historie lückenlos und unverändert ({0} Einträge)", payHist.event_count) : tr("⚠ Achtung: Die Historie wurde nachträglich verändert!")}
            </div>
            <Button variant="outline" size="sm" data-testid="history-csv" onClick={() => downloadHistoryCsv(payHist)} className="rounded-full">{tr("Als CSV herunterladen")}</Button>
          </div>
          <p className="text-xs text-[#A89A90] mb-3">{tr("Zeiten in deiner Ortszeit. Gespeichert wird in UTC, Einträge können nicht geändert oder gelöscht werden.")}</p>
          <div className="flex gap-3 items-start">
          <div className="flex-1 min-w-0 space-y-3">
          {payHist.orders.length === 0 && <p className="text-[#A89A90] py-10 text-center" data-testid="admin-no-orders">{t("admin.noOrders")}</p>}
          {payHist.orders.filter((o) => !oLetter || azLetter(o.craftsman_name) === oLetter).map((o) => (
            <div key={o._id} data-testid={`admin-order-${o._id}`} className={`rounded-lg border p-4 ${o.status === "pending" ? "border-[#E69625] bg-[#E69625]/5" : "border-[#EBDDD3] bg-white"}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-bold text-[#2D231F]" dir="ltr">{o.reference}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${o.status === "pending" ? "bg-[#E69625]/15 text-[#B27A1E]" : o.status === "confirmed" ? "bg-[#5C6646]/15 text-[#3F4A2E]" : "bg-[#8C7A70]/15 text-[#6B5A50]"}`}>{t(`credits.${o.status}`)}</span>
                  </div>
                  <Person label="Handwerker" name={o.craftsman_name} email={o.craftsman_email} />
                  <div className="text-sm text-[#8C7A70] mt-1">{o.credits} {t("credits.leads")} · {money(o.amount)}</div>
                </div>
                {o.status === "pending" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" data-testid={`confirm-order-${o._id}`} onClick={() => confirmOrder(o._id)} className="rounded-full gap-1.5 bg-[#5C6646] hover:bg-[#4A5238] text-white"><CheckCircle2 className="w-4 h-4" />{t("admin.confirm")}</Button>
                    <Button variant="outline" size="sm" data-testid={`cancel-order-${o._id}`} onClick={() => cancelOrder(o._id)} className="rounded-full gap-1.5"><Ban className="w-4 h-4" />{t("admin.cancel")}</Button>
                  </div>
                )}
              </div>
              <div className="mt-3 border-t border-[#EBDDD3] pt-2 space-y-1">
                {o.events.map((e) => (
                  <div key={e._id} className="text-xs text-[#5A4B45]">
                    <b>{tr(EVENT_LABEL[e.kind] || e.kind)}</b> · {fmtDate(e.at)} · {tr("durch")} {e.actor_name || "—"}{e.actor_email ? ` (${e.actor_email})` : ""}{e.actor_role === "admin" ? tr(" [Admin]") : e.actor_role === "craftsman" ? tr(" [Handwerker]") : ""}
                    {e.backfilled && <span className="text-[#A89A90]">{tr(" · nachgetragen aus Altbestand")}</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {payHist.adjustments.length > 0 && (
            <div className="pt-4">
              <h3 className="font-semibold text-[#2D231F] mb-2">{tr("Gutschriften und Korrekturen durch den Admin")}</h3>
              <div className="space-y-2">
                {payHist.adjustments.filter((e) => !oLetter || azLetter(e.craftsman_name) === oLetter).map((e) => (
                  <div key={e._id} className="rounded-lg border border-[#EBDDD3] bg-white p-3 text-xs text-[#5A4B45]">
                    <b>{tr(EVENT_LABEL[e.kind] || e.kind)}: {e.credits > 0 ? "+" : ""}{e.credits} {t("credits.leads")}</b> · {fmtDate(e.at)}
                    <Person label="Handwerker" name={e.craftsman_name} email={e.craftsman_email} />
                    <div className="mt-1">{tr("durch")} {e.actor_name || "—"}{e.actor_email ? ` (${e.actor_email})` : ""}{e.note ? ` · ${e.note}` : ""}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
          <AzBar items={payHist.orders} nameKey="craftsman_name" value={oLetter} onChange={setOLetter} testid="oaz" />
          </div>
        </TabsContent>

        <TabsContent value="contacts">
          <div className="flex gap-3 items-start">
          <div className="flex-1 min-w-0 space-y-3">
          <p className="text-sm text-[#8C7A70]">{tr("Nachrichten aus dem Kontaktformular (Fragen, Probleme, Beschwerden). Antworte per E-Mail und markiere sie danach als erledigt – so bleibt alles nachvollziehbar.")}</p>
          {contacts.length === 0 && <p className="text-[#A89A90] py-10 text-center" data-testid="admin-no-contacts">{tr("Noch keine Nachrichten.")}</p>}
          {contacts.filter((c) => !cnLetter || azLetter(c.name) === cnLetter).map((c) => (
            <div key={c._id} data-testid={`admin-contact-${c._id}`} className="rounded-lg border border-[#EBDDD3] bg-white p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Mail className="w-4 h-4 text-[#C65338]" />
                <span className="font-semibold text-[#2D231F]">{tr(({ question: "Frage", problem: "Problem melden", complaint: "Beschwerde", other: "Sonstiges" })[c.topic] || c.topic)}</span>
                <span className="text-xs text-[#8C7A70]">{fmtDate(c.created_at)}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.status === "new" ? "bg-[#B0413E]/10 text-[#B0413E]" : "bg-[#5C6646]/15 text-[#3F4A2E]"}`}>{c.status === "new" ? tr("Neu") : tr("Erledigt")}</span>
              </div>
              <Person label="Von" name={c.name} email={c.email} />
              <p className="mt-2 text-sm text-[#2D231F] whitespace-pre-wrap">{c.message}</p>
              {c.status === "done" ? (
                <p className="mt-2 text-xs text-[#8C7A70]">{tr("Erledigt am {0} von {1}", fmtDate(c.handled_at), c.handled_by)}{c.admin_note ? tr(" · Notiz: {0}", c.admin_note) : ""}</p>
              ) : (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <input data-testid={`contact-note-${c._id}`} value={cnotes[c._id] || ""} onChange={(e) => setCnotes((p) => ({ ...p, [c._id]: e.target.value }))} maxLength={1000} placeholder={tr("Notiz (z. B. per Mail geantwortet)")} className="flex-1 min-w-[200px] rounded-lg border border-[#EBDDD3] px-3 py-1.5 text-sm" />
                  <a href={`mailto:${c.email}?subject=${encodeURIComponent("Re: MyMoalem – " + (c.topic || ""))}`} data-testid={`contact-reply-${c._id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#C65338] text-[#C65338] px-4 h-9 text-sm font-semibold"><Mail className="w-4 h-4" />{tr("Antworten")}</a>
                  <Button size="sm" data-testid={`contact-done-${c._id}`} onClick={() => resolveContact(c._id)} className="rounded-full bg-[#5C6646] hover:bg-[#4a5337] text-white gap-1.5"><CheckCircle2 className="w-4 h-4" />{tr("Erledigt")}</Button>
                </div>
              )}
            </div>
          ))}
          </div>
          <AzBar items={contacts} nameKey="name" value={cnLetter} onChange={setCnLetter} testid="cnaz" />
          </div>
        </TabsContent>

        <TabsContent value="attempts">
          <div className="flex gap-3 items-start">
          <div className="flex-1 min-w-0 space-y-3">
          <p className="text-sm text-[#8C7A70]">{tr("Hier siehst du, wer versucht hat, Telefonnummern, E-Mail-Adressen oder Links in Texten zu hinterlassen (der Text wurde abgelehnt und nicht gespeichert). Wer es mehrfach versucht, kann gesperrt werden.")}</p>
          {attempts.length === 0 && <p className="text-[#A89A90] py-10 text-center" data-testid="admin-no-attempts">{tr("Keine Versuche.")}</p>}
          {attempts.filter((a) => !atLetter || azLetter(a.user_name) === atLetter).map((a) => (
            <div key={a._id} data-testid={`attempt-${a._id}`} className="rounded-lg border border-[#EBDDD3] bg-white p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Ban className="w-4 h-4 text-[#E69625]" />
                <span className="font-semibold text-[#2D231F]">{tr(({ phone: "Telefonnummer", email: "E-Mail-Adresse", link: "Link", social: "Messenger/Social Media" })[a.kind] || a.kind)}</span>
                <span className="text-xs text-[#8C7A70]">{fmtDate(a.created_at)}</span>
                <span className="px-2 py-0.5 rounded-full bg-[#F2E8DF] text-xs text-[#5A4B45]">{tr(({ job: "Auftrag", application: "Bewerbung", profile: "Profil", message: "Chat", review: "Bewertung", register: "Registrierung" })[a.endpoint] || a.endpoint)} · {a.field}</span>
              </div>
              <Person label="Nutzer" name={a.user_name || tr("(nicht angemeldet)")} email={a.user_email} />
              <p className="mt-1 text-sm text-[#5A4B45] italic">„{a.text}“</p>
              {!a.user_id && <div className="mt-2"><Button variant="ghost" size="sm" data-testid={`attempt-del-${a._id}`} onClick={() => delAttempt(a._id)} className="rounded-full gap-1.5 text-destructive"><Trash2 className="w-4 h-4" />{tr("Eintrag löschen")}</Button></div>}
              {a.user_id && (() => {
                const usr = users.find((x) => x._id === a.user_id);
                const n = attempts.filter((x) => x.user_id === a.user_id).length;
                return (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-[#8C7A70]">{n === 1 ? tr("1 Versuch von diesem Nutzer") : tr("{0} Versuche von diesem Nutzer", n)}</span>
                    {usr && usr.warning_count > 0 && <span data-testid={`warned-${a._id}`} className="px-2 py-0.5 rounded-full bg-[#E69625]/15 text-xs font-semibold text-[#B27A1E]">{tr("{0}. Mahnung", usr.warning_count)}{usr.last_warned_at ? tr(" am {0}", fmtDate(usr.last_warned_at)) : ""}</span>}
                    {usr && !usr.blocked && usr.role !== "admin" && (
                      <Button variant="ghost" size="sm" data-testid={`attempt-warn-${a._id}`} onClick={() => warnUser(usr)} className="rounded-full gap-1.5 text-[#B27A1E]"><Mail className="w-4 h-4" />{tr("{0}. Mahnung senden", (usr.warning_count || 0) + 1)}</Button>
                    )}
                    {usr && usr.blocked && <span className="px-2 py-0.5 rounded-full bg-[#B0413E]/10 text-xs font-semibold text-[#B0413E]">{t("admin.blocked")}</span>}
                    <Button variant="ghost" size="sm" data-testid={`attempt-del-${a._id}`} onClick={() => delAttempt(a._id)} className="rounded-full gap-1.5 text-destructive"><Trash2 className="w-4 h-4" />{tr("Eintrag löschen")}</Button>
                    {usr && (
                      <Button variant="ghost" size="sm" data-testid={`attempt-block-${a._id}`}
                        onClick={() => toggleBlock(usr._id, usr.name, usr.email, usr.blocked, "Mehrfach versucht, Kontaktdaten (Telefonnummer, E-Mail oder Link) auf MyMoalem zu hinterlassen, um die Plattform zu umgehen. Das verstößt gegen unsere Richtlinien.")}
                        className={`rounded-full gap-1.5 ${usr.blocked ? "text-[#5C6646]" : "text-[#B0413E]"}`}>
                        <Ban className="w-4 h-4" />{usr.blocked ? t("admin.unblock") : t("admin.block")}
                      </Button>
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
          </div>
          <AzBar items={attempts} nameKey="user_name" value={atLetter} onChange={setAtLetter} testid="atz" />
          </div>
        </TabsContent>

        <TabsContent value="blocks">
          <div className="flex gap-3 items-start">
          <div className="flex-1 min-w-0 space-y-3">
          <p className="text-sm text-[#8C7A70]">{tr("Alle Sperren und Freigaben mit Begründung, Datum und Mail-Status. Gesperrte E-Mail-Adressen können nicht erneut registriert werden.")}</p>
          {blockHist.length === 0 && <p className="text-[#A89A90] py-10 text-center" data-testid="admin-no-blocks">{tr("Noch keine Sperren.")}</p>}
          {blockHist.filter((e) => !bLetter || azLetter(e.user_name) === bLetter).map((e) => (
            <div key={e._id} data-testid={`block-event-${e._id}`} className="rounded-lg border border-[#EBDDD3] bg-white p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Ban className={`w-4 h-4 ${e.type === "blocked" ? "text-[#B0413E]" : e.type === "warned" ? "text-[#E69625]" : "text-[#5C6646]"}`} />
                <span className="font-semibold text-[#2D231F]">{e.type === "blocked" ? tr("Gesperrt") : e.type === "warned" ? tr("Mahnung per Mail") : tr("Sperre aufgehoben")}</span>
                <span className="text-xs text-[#8C7A70]">{fmtDate(e.created_at)}</span>
                {(e.type === "blocked" || e.type === "warned") && <span className="px-2 py-0.5 rounded-full bg-[#F2E8DF] text-xs text-[#5A4B45]">{e.mail_sent === true ? tr("Mail gesendet") : e.mail_sent === false ? tr("Mail fehlgeschlagen / nicht gesendet") : tr("ohne Mail")}</span>}
              </div>
              <Person label="Nutzer" name={e.user_name} email={e.user_email} />
              <Person label="Durchgeführt von" name={e.actor?.name} email={e.actor?.email} />
              {e.reason && <p className="text-sm text-[#5A4B45] mt-1 italic">„{e.reason}“</p>}
            </div>
          ))}
          </div>
          <AzBar items={blockHist} nameKey="user_name" value={bLetter} onChange={setBLetter} testid="baz" />
          </div>
        </TabsContent>

        <TabsContent value="reports">
          <div className="flex gap-3 items-start">
          <div className="flex-1 min-w-0 space-y-3">
          {reports.length === 0 && <p className="text-[#A89A90] py-10 text-center" data-testid="admin-no-reports">{t("admin.noReports")}</p>}
          {[...reports].sort(byName("customer_name")).filter((r) => !pLetter || azLetter(r.customer_name) === pLetter).map((r) => {
            const pending = r.status === "pending" || r.status === "auto_confirmed";
            return (
              <div key={r._id} data-testid={`admin-report-${r._id}`} className="rounded-lg border border-[#EBDDD3] bg-white p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Flag className="w-4 h-4 text-[#B0413E] shrink-0" />
                  <span className="font-semibold text-[#2D231F]">{r.job_title || "—"}</span>
                  <span className="px-2 py-0.5 rounded-full bg-[#E69625]/15 text-xs font-semibold text-[#B27A1E]">{r.report_count} {t("admin.reporters")}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${pending ? "bg-[#B0413E]/10 text-[#B0413E]" : "bg-[#F2E8DF] text-[#5A4B45]"}`}>{tr(REPORT_STATUS[r.status] || r.status)}</span>
                </div>
                <Person label="Kunde (Auftraggeber)" name={r.customer_name} email={r.customer_email} />
                <Person label="Gemeldet von (Handwerker)" name={r.reporter_name} email={r.reporter_email} />
                {r.reason && <p className="text-sm text-[#5A4B45] mt-1 italic">"{r.reason}"</p>}
                <div className="mt-3 border-t border-[#EBDDD3] pt-3" data-testid={`report-history-${r._id}`}>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#8C7A70] mb-2">{tr("Verlauf – wie es dazu kam")}</div>
                  <ol className="space-y-2 border-l-2 border-[#EBDDD3] ml-1.5 pl-4">
                    {(r.events || []).length === 0 && <li className="text-xs text-[#A89A90]">{tr("Meldung vom {0} (älterer Eintrag ohne Detailverlauf)", fmtDate(r.created_at))}</li>}
                    {(r.events || []).map((e) => { const x = reportEventText(e, tr); return (
                      <li key={e._id} className="relative text-sm">
                        <span className="absolute -left-[22px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#C65338]" />
                        <div className="font-semibold text-[#2D231F]">{x.title} <span className="font-normal text-xs text-[#8C7A70]">· {fmtDate(e.created_at)} · {x.who}</span></div>
                        <div className="text-[#5A4B45]">{x.text}</div>
                      </li>); })}
                  </ol>
                </div>
                {pending && (
                  <div className="mt-3 space-y-2">
                    <textarea data-testid={`report-note-${r._id}`} value={notes[r._id] || ""} onChange={(e) => setNotes((p) => ({ ...p, [r._id]: e.target.value }))} rows={2} maxLength={1000}
                      placeholder={tr("Begründung für deine Entscheidung (wird im Verlauf gespeichert, z. B. warum bestätigt oder abgelehnt)")}
                      className="w-full rounded-lg border border-[#EBDDD3] px-3 py-2 text-sm" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select value={sev[r._id] || "disable"} onValueChange={(v) => setSev((p) => ({ ...p, [r._id]: v }))}>
                        <SelectTrigger data-testid={`report-sev-${r._id}`} className="rounded-lg h-9 w-56 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="disable">{t("admin.sevDisable")}</SelectItem>
                          <SelectItem value="flag">{t("admin.sevFlag")}</SelectItem>
                          <SelectItem value="block">{t("admin.sevBlock")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" data-testid={`report-confirm-${r._id}`} onClick={() => resolveReport(r._id, "confirm", sev[r._id])} className="rounded-full bg-[#B0413E] hover:bg-[#8f3230] text-white gap-1.5"><CheckCircle2 className="w-4 h-4" />{t("admin.confirmFake")}</Button>
                      <Button size="sm" variant="outline" data-testid={`report-dismiss-${r._id}`} onClick={() => resolveReport(r._id, "dismiss")} className="rounded-full">{t("admin.dismiss")}</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
          <AzBar items={reports} nameKey="customer_name" value={pLetter} onChange={setPLetter} testid="paz" />
          </div>
        </TabsContent>
      </Tabs>
      <Dialog open={!!grantT} onOpenChange={(o) => { if (!o) setGrantT(null); }}>
        <DialogContent data-testid="grant-dialog">
          <DialogHeader><DialogTitle>{tr("Leads gutschreiben: {0}", grantT?.name || "")}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5A4B45]">{tr("Gutschriften sind keine Käufe. Damit sie später nachvollziehbar sind, wird Datum, Uhrzeit, deine Person und deine Begründung protokolliert (Credits- und Zahlungs-Verlauf). Bestellungen, die ein Handwerker bezahlt hat, bestätigst du weiterhin im Tab Zahlungen.")}</p>
          <label className="text-sm font-semibold text-[#2D231F]">{tr("Anzahl Leads")}</label>
          <input data-testid="grant-amount" type="number" min="1" max="1000" value={grantAmt} onChange={(e) => setGrantAmt(e.target.value)} className="w-32 rounded-lg border border-[#EBDDD3] px-3 py-1.5 text-sm" />
          <label className="text-sm font-semibold text-[#2D231F]">{tr("Begründung (Pflicht): warum und weshalb schreibst du die Leads gut?")}</label>
          <textarea data-testid="grant-reason" value={grantReason} onChange={(e) => setGrantReason(e.target.value)} rows={3} maxLength={1000}
            placeholder={tr("z. B. Kulanz nach Reklamation vom 03.11.; Fehler bei der Bestellung; Aktion für Neukunden …")}
            className="w-full rounded-lg border border-[#EBDDD3] px-3 py-2 text-sm" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setGrantT(null)}>{tr("Abbrechen")}</Button>
            <Button data-testid="grant-confirm" disabled={grantBusy || grantReason.trim().length < 10 || !(parseInt(grantAmt, 10) > 0)} onClick={doGrant} className="rounded-full bg-[#5C6646] hover:bg-[#4a5337] text-white">{tr("Gutschreiben")}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!blockT} onOpenChange={(o) => { if (!o) setBlockT(null); }}>
        <DialogContent data-testid="block-dialog">
          <DialogHeader><DialogTitle>{tr("Konto sperren: {0}", `${blockT?.name || ""}${blockT?.email ? ` (${blockT.email})` : ""}`)}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5A4B45]">{tr("Der Nutzer wird sofort abgemeldet und kann sich nicht mehr einloggen. Die E-Mail-Adresse wird gesperrt und kann nicht erneut registriert werden. Offene Aufträge werden deaktiviert.")}</p>
          <label className="text-sm font-semibold text-[#2D231F]">{tr("Festgestellte Verstöße gegen die Richtlinien (Pflicht, wird dem Nutzer in der Mail genannt)")}</label>
          <textarea data-testid="block-reason" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} rows={4} maxLength={2000}
            placeholder={tr("z. B. Mehrfach gefälschte Aufträge eingestellt; Handwerker wurden zur Zahlung außerhalb der Plattform gedrängt …")}
            className="w-full rounded-lg border border-[#EBDDD3] px-3 py-2 text-sm" />
          <label className="flex items-center gap-2 text-sm text-[#2D231F]"><input data-testid="block-mail" type="checkbox" checked={blockMail} onChange={(e) => setBlockMail(e.target.checked)} />{tr(" Mail mit Begründung an den Nutzer senden (empfohlen)")}</label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setBlockT(null)}>{tr("Abbrechen")}</Button>
            <Button data-testid="block-confirm" disabled={blockBusy || blockReason.trim().length < 10} onClick={doBlock} className="rounded-full bg-[#B0413E] hover:bg-[#8f3230] text-white">{tr("Jetzt sperren")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}